import { router, Link } from "@inertiajs/react"
import { AdminLayout } from "@/components/layouts/admin-layout"
import { MainContainer } from "@/components/ui/container"
import { DataTable } from "@/components/ui/data-table"
import type { AppliedSort, DataColumn } from "@/components/ui/data-table"
import { PageHeader } from "@/components/ui/headers"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { t } from "@/lib/i18n"

type AdminUserRow = {
  id: number
  name: string
  email: string
  confirmed: boolean
  confirmedAt: string | null
  lastActivityAt: string | null
  createdAt: string
  isAdmin: boolean
  isCurrent: boolean
}

type Pagination = {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
  previousPage: number | null
  nextPage: number | null
}

type Props = {
  users: AdminUserRow[]
  pagination: Pagination
  search: string
  sort: AppliedSort
}

function revokeAdmin(id: number) {
  if (!window.confirm(t("ui.react.pages.admin.users.index.revoke_confirm"))) return

  router.delete(`/users/${id}`, { preserveScroll: true })
}

const TABLE_PROPS = [ "users", "pagination", "search", "sort" ]

const columns: DataColumn<AdminUserRow>[] = [
  {
    accessorKey: "name",
    header: "Nombre",
    field: { type: "title", truncate: true },
    sortKey: "name",
  },
  {
    accessorKey: "email",
    header: "Email",
    field: { type: "text", truncate: true },
    sortKey: "email_address",
    mobile: "meta",
  },
  {
    accessorKey: "confirmed",
    header: t("ui.admin.users.confirmed"),
    field: { type: "boolean" },
  },
  {
    accessorKey: "lastActivityAt",
    header: t("ui.admin.users.last_activity"),
    field: { type: "datetime" },
    sortKey: "last_activity_at",
    mobile: "meta",
  },
  {
    accessorKey: "createdAt",
    header: "Creado",
    field: { type: "datetime" },
    sortKey: "created_at",
  },
  {
    accessorKey: "isAdmin",
    header: t("ui.react.pages.admin.users.index.admin"),
    mobile: "secondary",
    cell: ({ row }) =>
      row.original.isAdmin ? (
        <Badge variant="secondary">{t("ui.react.pages.admin.users.index.admin")}</Badge>
      ) : null,
  },
  {
    id: "actions",
    header: "",
    enableHiding: false,
    field: {
      type: "actions",
      // `text-destructive`, not `text-red-600`: state is a status token here,
      // never a raw Tailwind hue. See .claude/rules/system-design.md.
      render: (user) =>
        user.isAdmin && !user.isCurrent ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => revokeAdmin(user.id)}
          >
            {t("ui.react.pages.admin.users.index.revoke")}
          </Button>
        ) : null,
    },
  },
]

export default function AdminUsersIndex({ users, pagination, search, sort }: Props) {
  return (
    <AdminLayout>
      <MainContainer>
        <PageHeader
          title={t("ui.react.pages.admin.users.index.users")}
          description={`Mostrando ${users.length} de ${pagination.totalCount} usuarios`}
          actions={
            <Button asChild>
              <Link href="/users/new">{t("ui.react.pages.admin.users.index.create_admin")}</Link>
            </Button>
          }
        />

        <DataTable
          mode="server"
          url="/users"
          columns={columns}
          data={users}
          surface="card"
          search={search}
          sort={sort}
          pagination={pagination}
          reloadOnly={TABLE_PROPS}
          searchPlaceholder={t("ui.react.pages.admin.users.index.search_users")}
        />
      </MainContainer>
    </AdminLayout>
  )
}
